import { useRouter } from 'next/router'

export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'
export const POST_LOGIN_REDIRECT_HANDLED_KEY = 'postLoginRedirectHandled'

const safeGetSessionItem = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const safeSetSessionItem = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    // Ignore storage failures (private mode, restricted policies, etc.)
  }
}

const safeRemoveSessionItem = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // Ignore storage failures (private mode, restricted policies, etc.)
  }
}

export const hasPendingPostLoginRedirect = (): boolean => {
  if (typeof window === 'undefined') return false

  const hasRedirectUrl = !!safeGetSessionItem(POST_LOGIN_REDIRECT_KEY)
  const isAlreadyHandled =
    safeGetSessionItem(POST_LOGIN_REDIRECT_HANDLED_KEY) === 'true'

  return hasRedirectUrl && !isAlreadyHandled
}

type UsePostLoginRedirectT = {
  saveCurrentLocation: () => void
  clear: () => void
  restoreIfNeeded: () => boolean
}

export const usePostLoginRedirect = (): UsePostLoginRedirectT => {
  const router = useRouter()

  const saveCurrentLocation = () => {
    safeRemoveSessionItem(POST_LOGIN_REDIRECT_HANDLED_KEY)
    safeSetSessionItem(POST_LOGIN_REDIRECT_KEY, window.location.href)
  }

  const clear = () => {
    safeRemoveSessionItem(POST_LOGIN_REDIRECT_KEY)
    safeRemoveSessionItem(POST_LOGIN_REDIRECT_HANDLED_KEY)
  }

  // Restores the saved URL after successful login, when the current route is
  // not already the target. Returns true when a redirect is triggered.
  const restoreIfNeeded = (): boolean => {
    if (!hasPendingPostLoginRedirect()) {
      return false
    }

    const postLoginRedirect = safeGetSessionItem(POST_LOGIN_REDIRECT_KEY)
    if (!postLoginRedirect) return false

    let redirectPath: string

    try {
      const redirectUrl = new URL(postLoginRedirect)

      if (redirectUrl.origin !== window.location.origin) {
        safeRemoveSessionItem(POST_LOGIN_REDIRECT_KEY)
        return false
      }

      redirectPath =
        redirectUrl.pathname + redirectUrl.search + redirectUrl.hash
    } catch {
      // Ignore malformed redirect urls in session storage.
      safeRemoveSessionItem(POST_LOGIN_REDIRECT_KEY)
      return false
    }

    const currentPath =
      window.location.pathname + window.location.search + window.location.hash

    if (redirectPath === currentPath || redirectPath === '/') {
      safeRemoveSessionItem(POST_LOGIN_REDIRECT_KEY)
      return false
    }

    safeRemoveSessionItem(POST_LOGIN_REDIRECT_KEY)
    safeSetSessionItem(POST_LOGIN_REDIRECT_HANDLED_KEY, 'true')
    router.replace(redirectPath)
    return true
  }

  return {
    saveCurrentLocation,
    clear,
    restoreIfNeeded,
  }
}
